import React, { useState } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Badge,
    Button,
    FormGroup,
    Label,
    Input,
    Spinner,
} from 'reactstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import SweetAlert from 'react-bootstrap-sweetalert';
import TimelineHeader from '../../components/headers/TimelineHeader';
import usePropertyRecommendations from './hooks/usePropertyRecommendations';
import { PropertiesList } from './models/propertyModel';

const PropertiesRecommendation = () => {
    const {
        recommendations,
        searchLoading,
        searchError,
        hasChangesRent,
        hasChangesSale,
        pendingActions,
        searchProperty,
        addRecommendation,
        removeRecommendation,
        reorderRecommendations,
        savePositions,
        cancelChanges,
    } = usePropertyRecommendations();

    const [searchCode, setSearchCode] = useState<string>('');
    const [foundProperty, setFoundProperty] = useState<PropertiesList | null>(null);

    const [recType, setRecType] = useState<'for_rent' | 'for_sale'>('for_rent');
    const [recPosition, setRecPosition] = useState<number>(1);
    const [formError, setFormError] = useState<string>('');
    const [formSuccess, setFormSuccess] = useState<boolean>(false);

    const [sweetAlert, setSweetAlert] = useState<boolean>(false);
    const [alertProps, setAlertProps] = useState<any>({
        title: '',
        text: '',
        onConfirm: () => setSweetAlert(false),
        onCancel: () => setSweetAlert(false),
    });

    const resetSearchForm = () => {
        setSearchCode('');
        setFoundProperty(null);
        setFormError('');
        setFormSuccess(false);
    };

    // Search property by code
    const handleSearch = async () => {
        setFoundProperty(null);
        setFormError('');
        const property = await searchProperty(searchCode);
        if (property) {
            setFoundProperty(property);

            if (property.state === 'published') {
                setRecType('for_rent');
                const rentCount = recommendations.filter(r => r.marketplace_type === 'for_rent').length;
                setRecPosition(rentCount + 1);
            } else if (property.sale_state === 'published') {
                setRecType('for_sale');
                const saleCount = recommendations.filter(r => r.marketplace_type === 'for_sale').length;
                setRecPosition(saleCount + 1);
            }
        }
    };

    // When changing the type of recommendation in the form, adjust the suggested position to the end
    const handleTypeChange = (type: 'for_rent' | 'for_sale') => {
        setRecType(type);
        const count = recommendations.filter(r => r.marketplace_type === type).length;
        setRecPosition(count + 1);
    };

    // Save recommendation
    const handleSave = async () => {
        if (!foundProperty) return;
        setFormError('');
        setFormSuccess(false);

        const result = await addRecommendation(foundProperty, recType, recPosition);

        if (result.success) {
            setFormSuccess(true);
            setTimeout(() => {
                resetSearchForm();
            }, 1000);
        } else {
            setFormError(result.message || 'Error al agregar recomendación');
        }
    };

    // Confirmation of mass save of positions
    const handleConfirmSavePositions = (type: 'for_rent' | 'for_sale') => {
        const typeLabel = type === 'for_rent' ? 'Alquiler' : 'Venta';
        setAlertProps({
            warning: true,
            showCancel: true,
            confirmBtnBsStyle: 'success',
            cancelBtnBsStyle: 'secondary',
            confirmBtnText: 'Sí, guardar',
            cancelBtnText: 'No, cancelar',
            title: '¿Guardar posiciones?',
            text: `¿Estás seguro de guardar el nuevo orden de las recomendaciones de ${typeLabel}?`,
            onConfirm: async () => {
                setSweetAlert(false);
                const res = await savePositions(type);
                if (res.success) {
                    setAlertProps({
                        success: true,
                        title: 'Éxito',
                        text: `Las posiciones de las recomendaciones de ${typeLabel} se han guardado con éxito.`,
                        onConfirm: () => setSweetAlert(false),
                    });
                } else {
                    setAlertProps({
                        danger: true,
                        title: 'Error',
                        text: res.message || 'Ocurrió un error al guardar las posiciones.',
                        onConfirm: () => setSweetAlert(false),
                    });
                }
                setSweetAlert(true);
            },
            onCancel: () => setSweetAlert(false),
        });
        setSweetAlert(true);
    };

    // Confirmation of cancellation of local changes
    const handleCancelPositions = (type: 'for_rent' | 'for_sale') => {
        const typeLabel = type === 'for_rent' ? 'Alquiler' : 'Venta';
        setAlertProps({
            warning: true,
            showCancel: true,
            confirmBtnBsStyle: 'warning',
            cancelBtnBsStyle: 'secondary',
            confirmBtnText: 'Sí, cancelar',
            cancelBtnText: 'No, conservar',
            title: '¿Descartar cambios?',
            text: `¿Estás seguro de descartar el nuevo orden de posiciones de ${typeLabel}? Se restablecerán las posiciones guardadas en el servidor.`,
            onConfirm: () => {
                cancelChanges(type);
                setSweetAlert(false);
            },
            onCancel: () => setSweetAlert(false),
        });
        setSweetAlert(true);
    };

    // Confirmation of deletion
    const handleDeleteClick = (id: string, propertyCode: number, typeLabel: string) => {
        setAlertProps({
            warning: true,
            showCancel: true,
            confirmBtnBsStyle: 'danger',
            cancelBtnBsStyle: 'secondary',
            confirmBtnText: 'Sí, eliminar',
            cancelBtnText: 'No, cancelar',
            title: '¿Eliminar recomendación?',
            text: `¿Estás seguro de eliminar la propiedad con código ${propertyCode} de las recomendaciones de ${typeLabel}?`,
            onConfirm: async () => {
                setSweetAlert(false);
                const res = await removeRecommendation(id);
                if (res.success) {
                    setAlertProps({
                        success: true,
                        title: 'Eliminado',
                        text: 'La propiedad ha sido removida de las recomendaciones.',
                        onConfirm: () => setSweetAlert(false),
                    });
                } else {
                    setAlertProps({
                        danger: true,
                        title: 'Error',
                        text: res.message || 'Ocurrió un error al eliminar.',
                        onConfirm: () => setSweetAlert(false),
                    });
                }
                setSweetAlert(true);
            },
            onCancel: () => setSweetAlert(false),
        });
        setSweetAlert(true);
    };

    // Separate and order recommendations
    const rentRecommendations = recommendations
        .filter((r) => r.marketplace_type === 'for_rent')
        .sort((a, b) => a.position - b.position);

    const saleRecommendations = recommendations
        .filter((r) => r.marketplace_type === 'for_sale')
        .sort((a, b) => a.position - b.position);

    // Drag & Drop handlers
    const onDragEndRent = (result: any) => {
        const { source, destination } = result;
        if (!destination) return;
        if (source.index === destination.index) return;
        reorderRecommendations('for_rent', source.index, destination.index);
    };

    const onDragEndSale = (result: any) => {
        const { source, destination } = result;
        if (!destination) return;
        if (source.index === destination.index) return;
        reorderRecommendations('for_sale', source.index, destination.index);
    };

    return (
        <>
            {sweetAlert && (
                <SweetAlert {...alertProps}>
                    {alertProps.text || ''}
                </SweetAlert>
            )}

            <TimelineHeader name="Propiedades Recomendadas" parentName="Propiedades" />

            <Container className="mt--6" fluid>
                {/* Banner de aviso de sincronización */}
                {(hasChangesRent || hasChangesSale) && (
                    <Row className="mb-4">
                        <Col>
                            <Card className="shadow border-0 bg-warning">
                                <CardBody className="py-3">
                                    <div className="d-flex align-items-start">
                                        <i className="fas fa-exclamation-triangle mt-1 mr-3 text-white" />
                                        <div>
                                            <div>
                                                <span className="font-weight-bold text-white">
                                                    ¡Tienes cambios sin guardar!
                                                </span>
                                                <span className="text-white ml-2">
                                                    Has modificado la lista de recomendaciones. Haz clic en <strong>"Guardar Cambios"</strong> encima de la tabla correspondiente para sincronizar.
                                                </span>
                                            </div>
                                            {pendingActions && pendingActions.length > 0 && (
                                                <div className="text-white mt-2">
                                                    <strong style={{ fontSize: '0.85rem' }}>Acciones pendientes de sincronización:</strong>
                                                    <ul className="mb-0 mt-1 pl-4" style={{ fontSize: '0.85rem', listStyleType: 'disc' }}>
                                                        {pendingActions.map((action) => {
                                                            const tableLabel = action.marketplaceType === 'for_rent' ? 'Alquiler' : 'Venta';
                                                            const actionLabel = action.actionType === 'add' ? 'agregada' : 'eliminada';
                                                            return (
                                                                <li key={action.id} className="mt-1">
                                                                    Propiedad con código <strong>{action.propertyCode}</strong> fue <strong>{actionLabel}</strong> en la tabla de <strong>{tableLabel}</strong>.
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Buscador de Propiedad en la Vista */}
                <Row className="mb-4">
                    <Col>
                        <Card className="shadow border-0">
                            <CardHeader className="border-0 pb-1">
                                <h3 className="mb-0">Agregar Propiedad a Recomendaciones</h3>
                            </CardHeader>
                            <CardBody className="pt-0">
                                {!foundProperty ? (
                                    <Row className="align-items-end">
                                        <Col md="6" lg="5">
                                            <FormGroup className="mb-0">
                                                <Label className="form-control-label font-weight-bold" htmlFor="search-code-input">
                                                    Código
                                                </Label>
                                                <div className="d-flex">
                                                    <Input
                                                        id="search-code-input"
                                                        type="text"
                                                        placeholder="Ingresa el código de la propiedad"
                                                        value={searchCode}
                                                        onChange={(e) => setSearchCode(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                                        className="mr-2"
                                                    />
                                                    <Button color="primary" onClick={handleSearch} disabled={searchLoading} className="d-flex align-items-center">
                                                        {searchLoading ? <Spinner size="sm" className="mr-2" /> : <i className="fas fa-search mr-2" />} Buscar
                                                    </Button>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                        {searchError && (
                                            <Col md="6" lg="7" className="mt-2 mt-md-0">
                                                <div className="text-danger font-weight-bold" style={{ fontSize: '0.85rem' }}>
                                                    <i className="fas fa-exclamation-circle mr-1" />
                                                    {searchError}
                                                </div>
                                            </Col>
                                        )}
                                    </Row>
                                ) : (
                                    <div className="bg-secondary p-3 rounded">
                                        <Row className="align-items-center">
                                            {/* Preview de Propiedad */}
                                            <Col lg="4" md="6" className="mb-3 mb-lg-0">
                                                <div className="d-flex align-items-center">
                                                    <img
                                                        alt="vista-previa"
                                                        width={90}
                                                        height={60}
                                                        src={
                                                            foundProperty.cover && !foundProperty.cover.endsWith('null')
                                                                ? foundProperty.cover
                                                                : 'https://storage.googleapis.com/assets-us-east4-propilatam-dev/properties/default/default_1.jpg'
                                                        }
                                                        className="rounded mr-3"
                                                        style={{ objectFit: 'cover' }}
                                                    />
                                                    <div className="d-flex flex-column text-truncate" style={{ maxWidth: '190px' }}>
                                                        <span className="font-weight-bold text-dark text-truncate" style={{ fontSize: '0.9rem' }}>
                                                            {foundProperty.name || 'Propiedad sin nombre'}
                                                        </span>
                                                        <small className="text-primary font-weight-bold mb-1">
                                                            Código: {foundProperty.code}
                                                        </small>
                                                        <small className="text-muted text-truncate" style={{ fontSize: '0.75rem' }}>
                                                            {foundProperty.address}
                                                        </small>
                                                    </div>
                                                </div>
                                            </Col>

                                            {/* Tipo de recomendación */}
                                            <Col lg="3" md="6" className="mb-3 mb-lg-0">
                                                <FormGroup className="mb-0">
                                                    <Label className="form-control-label font-weight-bold mb-1" style={{ fontSize: '0.8rem' }}>Operación</Label>
                                                    <div className="d-flex gap-3 mt-1">
                                                        <div className="custom-control custom-radio mr-3">
                                                            <input
                                                                className="custom-control-input"
                                                                type="radio"
                                                                id="radio-rent"
                                                                name="recommendation-type"
                                                                checked={recType === 'for_rent'}
                                                                onChange={() => handleTypeChange('for_rent')}
                                                                disabled={foundProperty.state !== 'published'}
                                                            />
                                                            <label className="custom-control-label" htmlFor="radio-rent" style={{ fontSize: '0.85rem' }}>
                                                                Alquiler
                                                            </label>
                                                        </div>
                                                        <div className="custom-control custom-radio">
                                                            <input
                                                                className="custom-control-input"
                                                                type="radio"
                                                                id="radio-sale"
                                                                name="recommendation-type"
                                                                checked={recType === 'for_sale'}
                                                                onChange={() => handleTypeChange('for_sale')}
                                                                disabled={foundProperty.sale_state !== 'published'}
                                                            />
                                                            <label className="custom-control-label" htmlFor="radio-sale" style={{ fontSize: '0.85rem' }}>
                                                                Venta
                                                            </label>
                                                        </div>
                                                    </div>
                                                </FormGroup>
                                            </Col>

                                            {/* Posición */}
                                            <Col lg="2" md="4" className="mb-3 mb-md-0">
                                                <FormGroup className="mb-0">
                                                    <Label className="form-control-label font-weight-bold mb-1" htmlFor="pos-selector-input" style={{ fontSize: '0.8rem' }}>
                                                        Posición
                                                    </Label>
                                                    <Input
                                                        id="pos-selector-input"
                                                        type="number"
                                                        min={1}
                                                        max={(recType === 'for_rent' ? rentRecommendations : saleRecommendations).length + 1}
                                                        value={recPosition}
                                                        onChange={(e) => setRecPosition(Math.max(1, parseInt(e.target.value) || 1))}
                                                        style={{ height: '32px' }}
                                                    />
                                                </FormGroup>
                                            </Col>

                                            {/* Acciones de guardar / cancelar */}
                                            <Col lg="3" md="8" className="text-right">
                                                <Button color="secondary" size="sm" onClick={resetSearchForm} className="mr-2" disabled={searchLoading}>
                                                    Cancelar
                                                </Button>
                                                <Button color="primary" size="sm" onClick={handleSave} disabled={formSuccess || searchLoading}>
                                                    {formSuccess ? 'Agregado ✓' : 'Guardar'}
                                                </Button>
                                            </Col>
                                        </Row>

                                        {/* Alertas del formulario inline */}
                                        {formError && (
                                            <div className="text-danger font-weight-bold mt-3 mb-0" style={{ fontSize: '0.85rem' }}>
                                                <i className="fas fa-exclamation-triangle mr-2" />
                                                {formError}
                                            </div>
                                        )}
                                        {formSuccess && (
                                            <div className="text-success font-weight-bold mt-3 mb-0" style={{ fontSize: '0.85rem' }}>
                                                <i className="fas fa-check-circle mr-2" />
                                                ¡Propiedad recomendada agregada con éxito!
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>



                {/* Tablas de Recomendaciones lado a lado en pantallas grandes */}
                <Row className="mb-4">
                    {/* Tabla de Recomendaciones en Alquiler */}
                    <Col xs="12" xl="6" className="mb-4 mb-xl-0">
                        <Card className="shadow border-0">
                            <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                                <div>
                                    <h3 className="mb-0">Recomendaciones en Alquiler ({rentRecommendations.length})</h3>
                                    <small className="text-muted">
                                        <i className="fas fa-info-circle mr-1" /> Arrastra las filas para reordenar sus posiciones.
                                    </small>
                                </div>
                                {hasChangesRent && (
                                    <div className="d-flex">
                                        <Button
                                            color="secondary"
                                            size="sm"
                                            onClick={() => handleCancelPositions('for_rent')}
                                            disabled={searchLoading}
                                            className="mr-2"
                                        >
                                            <i className="fas fa-undo mr-1" /> Descartar Cambios
                                        </Button>
                                        <Button
                                            color="success"
                                            size="sm"
                                            onClick={() => handleConfirmSavePositions('for_rent')}
                                            disabled={searchLoading}
                                        >
                                            <i className="fas fa-save mr-1" /> Guardar Cambios
                                        </Button>
                                    </div>
                                )}
                            </CardHeader>

                            {rentRecommendations.length === 0 ? (
                                <CardBody className="text-center py-5">
                                    <div className="text-muted mb-3" style={{ fontSize: 32 }}>
                                        <i className="fas fa-search-minus" />
                                    </div>
                                    <h4 className="text-muted">No hay recomendaciones de alquiler</h4>
                                </CardBody>
                            ) : (
                                <DragDropContext onDragEnd={onDragEndRent}>
                                    <Table className="align-items-center table-flush" responsive>
                                        <thead className="thead-light">
                                            <tr>
                                                <th scope="col" style={{ width: '40px' }} />
                                                <th scope="col" className="text-center" style={{ width: '80px' }}>Posición</th>
                                                <th scope="col" style={{ width: '120px' }}>Portada</th>
                                                <th scope="col" style={{ width: '100px' }}>Código</th>
                                                <th scope="col">Propiedad</th>
                                                <th scope="col" style={{ width: '150px' }}>Precio</th>
                                                <th scope="col" className="text-right" style={{ width: '80px' }} />
                                            </tr>
                                        </thead>
                                        <Droppable droppableId="rent-droppable">
                                            {(provided, snapshot) => (
                                                <tbody {...provided.droppableProps} ref={provided.innerRef}>
                                                    {rentRecommendations.map((rec, index) => {
                                                        const formattedPrice = `$${rec.property.price?.toLocaleString() || 0} USD`;
                                                        return (
                                                            <Draggable key={rec.id} draggableId={rec.id} index={index}>
                                                                {(provided, snapshot) => (
                                                                    <tr
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        style={{
                                                                            ...provided.draggableProps.style,
                                                                            background: snapshot.isDragging ? '#f8f9fa' : 'transparent',
                                                                        }}
                                                                    >
                                                                        <td {...provided.dragHandleProps} className="text-center align-middle" style={{ width: '40px', cursor: 'grab' }}>
                                                                            <i className="fas fa-grip-vertical text-muted" />
                                                                        </td>
                                                                        <td className="text-center font-weight-bold align-middle">
                                                                            <span className="badge badge-secondary py-2 px-3" style={{ fontSize: '0.9rem' }}>
                                                                                {rec.position}
                                                                            </span>
                                                                        </td>
                                                                        <td className="align-middle">
                                                                            <img
                                                                                alt="portada"
                                                                                width={90}
                                                                                height={60}
                                                                                src={
                                                                                    rec.property.cover && !rec.property.cover.endsWith('null')
                                                                                        ? rec.property.cover
                                                                                        : 'https://storage.googleapis.com/assets-us-east4-propilatam-dev/properties/default/default_1.jpg'
                                                                                }
                                                                                className="rounded"
                                                                                style={{ objectFit: 'cover' }}
                                                                            />
                                                                        </td>
                                                                        <td className="align-middle">
                                                                            <a
                                                                                href={`/properties/admin/${rec.property.id}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="font-weight-bold text-primary"
                                                                            >
                                                                                {rec.property.code}
                                                                            </a>
                                                                        </td>
                                                                        <td className="align-middle">
                                                                            <div className="d-flex flex-column">
                                                                                <span className="font-weight-bold text-dark">
                                                                                    {rec.property.name || 'Propiedad sin nombre'}
                                                                                </span>
                                                                                <small className="text-muted text-truncate" style={{ maxWidth: '300px' }}>
                                                                                    <i className="fas fa-map-marker-alt mr-1" />
                                                                                    {rec.property.address}
                                                                                </small>
                                                                            </div>
                                                                        </td>
                                                                        <td className="font-weight-bold align-middle">
                                                                            {formattedPrice}
                                                                        </td>
                                                                        <td className="text-right align-middle">
                                                                            <Button
                                                                                size="sm"
                                                                                color="danger"
                                                                                outline
                                                                                onClick={() => handleDeleteClick(rec.id, rec.property.code, 'Alquiler')}
                                                                                className="py-1 px-2"
                                                                                title="Eliminar de recomendados"
                                                                                disabled={searchLoading}
                                                                            >
                                                                                <i className="fas fa-trash" />
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {provided.placeholder}
                                                </tbody>
                                            )}
                                        </Droppable>
                                    </Table>
                                </DragDropContext>
                            )}
                        </Card>
                    </Col>
                    {/*  Tabla de Recomendaciones en Venta */}
                    <Col xs="12" xl="6" className="mb-4 mb-xl-0">
                        <Card className="shadow border-0">
                            <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                                <div>
                                    <h3 className="mb-0">Recomendaciones en Venta ({saleRecommendations.length})</h3>
                                    <small className="text-muted">
                                        <i className="fas fa-info-circle mr-1" /> Arrastra las filas para reordenar sus posiciones.
                                    </small>
                                </div>
                                {hasChangesSale && (
                                    <div className="d-flex">
                                        <Button
                                            color="secondary"
                                            size="sm"
                                            onClick={() => handleCancelPositions('for_sale')}
                                            disabled={searchLoading}
                                            className="mr-2"
                                        >
                                            <i className="fas fa-undo mr-1" /> Descartar Cambios
                                        </Button>
                                        <Button
                                            color="success"
                                            size="sm"
                                            onClick={() => handleConfirmSavePositions('for_sale')}
                                            disabled={searchLoading}
                                        >
                                            <i className="fas fa-save mr-1" /> Guardar Cambios
                                        </Button>
                                    </div>
                                )}
                            </CardHeader>

                            {saleRecommendations.length === 0 ? (
                                <CardBody className="text-center py-5">
                                    <div className="text-muted mb-3" style={{ fontSize: 32 }}>
                                        <i className="fas fa-search-minus" />
                                    </div>
                                    <h4 className="text-muted">No hay recomendaciones de venta</h4>
                                </CardBody>
                            ) : (
                                <DragDropContext onDragEnd={onDragEndSale}>
                                    <Table className="align-items-center table-flush" responsive>
                                        <thead className="thead-light">
                                            <tr>
                                                <th scope="col" style={{ width: '40px' }} />
                                                <th scope="col" className="text-center" style={{ width: '80px' }}>Posición</th>
                                                <th scope="col" style={{ width: '120px' }}>Portada</th>
                                                <th scope="col" style={{ width: '100px' }}>Código</th>
                                                <th scope="col">Propiedad</th>
                                                <th scope="col" style={{ width: '150px' }}>Precio</th>
                                                <th scope="col" className="text-right" style={{ width: '80px' }} />
                                            </tr>
                                        </thead>
                                        <Droppable droppableId="sale-droppable">
                                            {(provided, snapshot) => (
                                                <tbody {...provided.droppableProps} ref={provided.innerRef}>
                                                    {saleRecommendations.map((rec, index) => {
                                                        const formattedPrice = rec.property.sale_price
                                                            ? `$${rec.property.sale_price.toLocaleString()} USD`
                                                            : 'No asignado';
                                                        return (
                                                            <Draggable key={rec.id} draggableId={rec.id} index={index}>
                                                                {(provided, snapshot) => (
                                                                    <tr
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        style={{
                                                                            ...provided.draggableProps.style,
                                                                            background: snapshot.isDragging ? '#f8f9fa' : 'transparent',
                                                                        }}
                                                                    >
                                                                        <td {...provided.dragHandleProps} className="text-center align-middle" style={{ width: '40px', cursor: 'grab' }}>
                                                                            <i className="fas fa-grip-vertical text-muted" />
                                                                        </td>
                                                                        <td className="text-center font-weight-bold align-middle">
                                                                            <span className="badge badge-secondary py-2 px-3" style={{ fontSize: '0.9rem' }}>
                                                                                {rec.position}
                                                                            </span>
                                                                        </td>
                                                                        <td className="align-middle">
                                                                            <img
                                                                                alt="portada"
                                                                                width={90}
                                                                                height={60}
                                                                                src={
                                                                                    rec.property.cover && !rec.property.cover.endsWith('null')
                                                                                        ? rec.property.cover
                                                                                        : 'https://storage.googleapis.com/assets-us-east4-propilatam-dev/properties/default/default_1.jpg'
                                                                                }
                                                                                className="rounded"
                                                                                style={{ objectFit: 'cover' }}
                                                                            />
                                                                        </td>
                                                                        <td className="align-middle">
                                                                            <a
                                                                                href={`/properties/admin/${rec.property.id}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="font-weight-bold text-primary"
                                                                            >
                                                                                {rec.property.code}
                                                                            </a>
                                                                        </td>
                                                                        <td className="align-middle">
                                                                            <div className="d-flex flex-column">
                                                                                <span className="font-weight-bold text-dark">
                                                                                    {rec.property.name || 'Propiedad sin nombre'}
                                                                                </span>
                                                                                <small className="text-muted text-truncate" style={{ maxWidth: '300px' }}>
                                                                                    <i className="fas fa-map-marker-alt mr-1" />
                                                                                    {rec.property.address}
                                                                                </small>
                                                                            </div>
                                                                        </td>
                                                                        <td className="font-weight-bold align-middle">
                                                                            {formattedPrice}
                                                                        </td>
                                                                        <td className="text-right align-middle">
                                                                            <Button
                                                                                size="sm"
                                                                                color="danger"
                                                                                outline
                                                                                onClick={() => handleDeleteClick(rec.id, rec.property.code, 'Venta')}
                                                                                className="py-1 px-2"
                                                                                title="Eliminar de recomendados"
                                                                                disabled={searchLoading}
                                                                            >
                                                                                <i className="fas fa-trash" />
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {provided.placeholder}
                                                </tbody>
                                            )}
                                        </Droppable>
                                    </Table>
                                </DragDropContext>
                            )}
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default PropertiesRecommendation;